import { HelpBox } from "@/components/atoms";
import {
  ActivePage,
  Audience, AudienceSegment,
  AudienceSummary,
  AudienceTravelTime,
  AudienceType,
  Breadcrumb,
  BuildAudience,
  UploadAudience,
  Variable,
} from "@/types";
import { uploadModeOptions } from "./constants";

const client = process.env.REACT_APP_CLIENT || "core";

export const defaultAudience: Audience = {
  audience_id: "",
  audience_name: "",
  client: client,
  created_at: "",
  segment: false,
  segments: [],
  audience_type: "build",
  audience_geo: "cube",
  build_mode: "balanced",
  operator: "AND",
  var_groups: [{ id: 1, variables: [], operator: "AND" }],
  export: true,
  project_name: null,
  shared: false,
  status: "idle",
};

export const defaultBuildAudience: BuildAudience = {
  audience_id: "",
  audience_name: "",
  client: client,
  created_at: "",
  segment: false,
  segments: [],
  audience_type: "build",
  audience_geo: "cube",
  build_mode: "balanced",
  operator: "AND",
  var_groups: [{ id: 1, variables: [], operator: "AND" }],
  export: true,
  project_name: null,
  shared: false,
  status: "idle",
};

export const defaultUploadAudience: UploadAudience = {
  audience_id: "",
  audience_name: "",
  client: client,
  created_at: "",
  segment: false,
  segments: [],
  audience_type: "upload",
  audience_geo: "postcode",
  upload_file: null,
  export: true,
  project_name: null,
  shared: false,
  status: "idle",
};

export const defaultTravelTime: AudienceTravelTime = {
  mode: 'combined',
  transportation: 'driving',
  duration: 15,
  departure_day: 'weekday',
  departure_time: 'morning'
}


export const defaultVariableGroup = (id: number) => {
  return {
    id: id,
    variables: [],
    operator: "AND",
  };
};

/**
 * Build breadcrumbs for an audience page
 */
export function getAudienceBreadcrumb(
  activePage: ActivePage | null,
  audiencePath: string,
  audience: Audience | null,
  loading: boolean,
): Breadcrumb[] {

  const ROUTE_LABELS: Record<string, string> = {
    'upload': 'Uploads',
    'insight': 'Insights',
    'export': 'Export',
    'new': 'New'
  };

  if (!activePage) return [];
  if (loading) return activePage.breadcrumbs;
  const breadcrumbs = [ ...activePage.breadcrumbs ];

  // Extract the parts of the path and convert to a breadcrumb
  const paths = audiencePath.replace(activePage.path, '').split('/').filter(Boolean);
  paths.forEach((path, index) => {
    let label = ROUTE_LABELS[path] || path;

    // If we have the audience name we use it here
    if (audience && audience.audience_name !== "" && index === 0) {
      label = audience.audience_name
    }

    breadcrumbs.push({title: label, path: `${activePage.path}/${paths.slice(0, index + 1).join('/')}`})
  })

  return breadcrumbs
}

/**
 * Filter a list of audiences using search terms, multiple filters and sort
 */
export const filterAudiences = (
  audiences: Audience[] | null,
  searchText?: string,
  projectFilter?: string,
  sortBy?: string,
  filterBy?: string,
  filterKey?: string,
) => {
  // Filter by search text
  let filteredAudiences = audiences?.filter((audience) => {
      if (audience.segment && searchText !== "") {
        return (audience.audience_name.toLowerCase().includes(searchText?.toLowerCase() || ""))
          || (audience.segments.some(x => x.segment_name.toLowerCase().includes(searchText?.toLowerCase() || "")))
      } else {
        return audience.audience_name
          .toLowerCase()
          .includes(searchText?.toLowerCase() || "")
      }
    }

  );

  // Filter by project
  if (projectFilter) {
    filteredAudiences = filteredAudiences?.filter(
      (audience) => audience.project_name === projectFilter
    );
  }

  // Apply filter
  if (filterBy && filterKey) {
    filteredAudiences = filteredAudiences?.filter(
      (audience) => audience[filterKey as keyof Audience] === filterBy
    );
  }

  // Apply sorting
  if (sortBy) {
    if (sortBy === "name") {
      filteredAudiences?.sort((a, b) =>
        a.audience_name.localeCompare(b.audience_name)
      );
    } else if (sortBy === "date") {
      filteredAudiences?.sort(
        (a, b) =>
          new Date(b.created_at || 0).getTime() -
          new Date(a.created_at || 0).getTime()
      );
    }
  } else {
    // Make sure the audiences with no project are always shown first unless there is a sort
    filteredAudiences = [
      ...(filteredAudiences?.filter((x) => x.project_name === null) || []),
      ...(filteredAudiences?.filter((x) => x.project_name !== null) || []),
    ];
  }

  return filteredAudiences;
};

export const checkAudienceSize = (summary: AudienceSummary) => {
  if (summary.num_postcodes !== undefined && summary.num_postcodes > 0) {
    return true;
  } else return summary.num_sectors !== undefined && summary.num_sectors > 0;
};


export const checkVariables = (audience: BuildAudience, variables: Variable[]) => {
  if (!variables || variables.length === 0) return true;

  const audienceVariables = audience.var_groups.map(x => x.variables).flat();
  const variableIds = new Set(variables.map(v => v.tag_id));
  return audienceVariables.every(v => variableIds.has(v.tag_id));
}

export const checkVariable = (selection: Variable, variables: Variable[]) => {
  return variables.map(v => v.tag_id).includes(selection.tag_id);
}

export const toAudienceType = (value: string): AudienceType | undefined => {
  const audienceTypes = ['build', 'upload', 'mindset'];
  if (audienceTypes.includes(value as AudienceType)) {
    return value as AudienceType
  } else {
    return undefined
  }
}

export const checkAudience = (audience: Audience): boolean => {

  const client = process.env.REACT_APP_CLIENT || 'core';

  return audience.client === client;
}

export const getAudienceIcon = (mode: string | undefined) => {
  const option = uploadModeOptions.find(x => x.value === mode)
  if (option) {
    const Component = option.iconComponent
    return (
      <HelpBox title={option.label}>
        <Component color="info" style={{ fontSize: 20 }}/>
      </HelpBox>
    )
  }
}

export const createBuildDefinition = (audience: BuildAudience) => {
  if (!audience) return '';

  const definition = {
    mode: audience.build_mode,
    op: audience.operator,
    groups: audience.var_groups.map((group) => ({
      op: group.operator,
      vars: group.variables.map((variable) => variable.tag_id).sort((a: number, b: number) => a - b),
    }))
  }
  return JSON.stringify(definition);
}

export const createUploadDefinition = (audience: UploadAudience) => {
  if (!audience) return '';

  const definition = {
    mode: audience.upload_mode,
    travel_time: audience.travel_time,
    upload_file: audience.upload_file?.name
  }
  return JSON.stringify(definition);
}

export const extractSegments = (summary: AudienceSummary[]): AudienceSegment[] => {
  return summary.map((x, index) => ({
    segment_id: index + 1,
    segment_name: x.segment ?? `Segment ${index + 1}`,
  }));
};
